/**
 * Отбор персональных проблемных слов.
 *
 * Освоенное слово в режим не попадает: показывать здесь то, что уже даётся
 * без усилий, значит тратить самое дорогое — время на слабых местах (ТЗ §7.3).
 *
 * Порог отбора — освоенность, а не слабость: строгий порог слабости (0.5)
 * после нескольких сессий не проходит почти никто, и режим оказался бы
 * недоступен именно тогда, когда работа над ошибками нужнее всего. Внутри
 * набора порядок задают приоритет и слабость: самое проблемное идёт первым.
 */

import type { MasteryStat, MasteryUnit } from '@type2x/shared-contracts';
import { MASTERED_THRESHOLD, WEAK_THRESHOLD } from '@type2x/shared-contracts';
import {
  computePriority,
  rankUnits,
  type LearningConfig,
  type PriorityContext,
} from '@type2x/learning-engine';

import type { MistakeSnapshot } from '../contracts/mistakes.js';
import { MIN_DRILL_LENGTH, MISTAKES_CAPACITY } from '../contracts/mistakes.js';

/** Единицы, с которыми режиму есть что делать. */
function candidates(snapshot: MistakeSnapshot): MasteryUnit[] {
  return Object.values(snapshot.units).filter((unit) => {
    // Одиночное нажатие циклу не поддаётся: у него нет ни частей, ни
    // применения в предложении. Слабой клавишей занимаются «Основы».
    if ([...unit.content].length < MIN_DRILL_LENGTH) return false;

    const stat: MasteryStat | undefined = snapshot.stats[unit.id];
    // Единица без попыток не является проблемной: она просто не встречалась.
    return stat !== undefined && stat.attempts > 0 && stat.masteryScore < MASTERED_THRESHOLD;
  });
}

/**
 * Отбирает материал микросессии.
 *
 * Контекст расчёта передаётся снаружи: он зависит от раскладки и истории
 * контрольных прогонов, а их знает слой оркестрации, а не режим
 * (docs/ARCHITECTURE.md §5).
 *
 * @param snapshot состояние навыка;
 * @param config конфигурация обучающего алгоритма;
 * @param context общий контекст расчёта приоритета;
 * @param limit сколько слов взять в микросессию.
 */
export function selectMistakeUnits(
  snapshot: MistakeSnapshot,
  config: LearningConfig,
  context: PriorityContext,
  limit: number = MISTAKES_CAPACITY,
): MasteryUnit[] {
  const ranked = rankUnits(
    candidates(snapshot).map((unit) =>
      computePriority(unit, snapshot.stats[unit.id]!, config, context),
    ),
  );

  const isWeak = (unit: MasteryUnit): boolean =>
    snapshot.stats[unit.id]!.masteryScore < WEAK_THRESHOLD;

  const units = ranked.map((result) => result.unit);
  return [...units.filter(isWeak), ...units.filter((unit) => !isWeak(unit))].slice(0, limit);
}

/** Есть ли над чем работать в режиме «Мои ошибки». */
export function hasMistakes(snapshot: MistakeSnapshot): boolean {
  return candidates(snapshot).length > 0;
}
